import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchSuppliers } from '../../api/suppliers';
import { fetchSubServices } from '../../api/subServices';
import { getAllLookups } from '../../api/lookups';
import { UtilityType, CreateUtilityServiceDTO, Supplier, SubService } from '../../types/utility.types';
import { Lookup } from '../../types/lookup.types';
import { displayError } from '../../utils/errorHandler';

interface AddUtilityServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: () => void;
  propertyId: string;
  clientId: string;
}

interface FormDataType extends Omit<CreateUtilityServiceDTO, 'contractStartDate' | 'contractEndDate'> {
  contractStartDate: Date | null;
  contractEndDate: Date | null;
}

const utilityTypes: UtilityType[] = [
  'Gas',
  'Electricity',
  'Water',
  'Telecoms',
  'Waste',
  'Merchant Services',
  'PPA Export Contract',
  'EV',
  'Renewable',
  'Insurance'
];

// Status options will be fetched from lookups API

const AddUtilityServiceDialog: React.FC<AddUtilityServiceDialogProps> = ({
  open,
  onClose,
  onAdd,
  propertyId,
  clientId
}) => {
  const [formData, setFormData] = useState<FormDataType>({
    serviceType: 'Gas',
    accountNumber: '',
    status: '',
    supplier: '',
    meterDetails: {
      meterType: '',
      location: ''
    },
    subServices: [],
    contractStartDate: null,
    contractEndDate: null,
    notes: '',
    currentRate: '',
    eacAq: ''
  });
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [availableSubServices, setAvailableSubServices] = useState<SubService[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        const parentObj = prev[parent as keyof FormDataType];
        
        if (parentObj && typeof parentObj === 'object' && !Array.isArray(parentObj)) {
          return {
            ...prev,
            [parent]: {
              ...parentObj,
              [child]: value
            }
          };
        }
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubServiceChange = (subServiceId: string) => {
    setFormData((prev) => ({
      ...prev,
      subServices: prev.subServices?.includes(subServiceId)
        ? prev.subServices.filter(id => id !== subServiceId)
        : [...(prev.subServices || []), subServiceId]
    }));
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        const [suppliersData, subServicesData, lookupsData] = await Promise.all([
          fetchSuppliers(),
          formData.serviceType ? fetchSubServices(formData.serviceType) : Promise.resolve([]),
          getAllLookups()
        ]);
        
        // Extract status options from lookups
        const statusLookups = lookupsData.filter((lookup: Lookup) => 
          lookup.type === 'UtilityServiceStatus' && lookup.active
        );
        setStatusOptions(statusLookups.map((lookup: Lookup) => lookup.value));
        
        
        setSuppliers(suppliersData);
        setAvailableSubServices(subServicesData);
        
      } catch (error) {
        const parsed = displayError(error, 'Failed to load required data');
        setError(parsed.message);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadData();
    }
  }, [open, formData.serviceType]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white' }}>Add Utility Service</DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 3, color: 'white' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Service Type"
              name="serviceType"
              value={formData.serviceType}
              onChange={handleSelectChange}
              required
            >
              {utilityTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Supplier"
              name="supplier"
              value={formData.supplier}
              onChange={handleSelectChange}
              required
              disabled={loading}
            >
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                suppliers.map((supplier) => (
                  <MenuItem key={supplier._id} value={supplier._id}>
                    {supplier.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Account Number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleSelectChange}
              required
            >
              {statusOptions.map((status: string) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Contract Start Date"
                value={formData.contractStartDate}
                onChange={(date) => {
                  setFormData(prev => ({
                    ...prev,
                    contractStartDate: date
                  }));
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main !important',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6) !important',
                      },
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87) !important',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Contract End Date"
                value={formData.contractEndDate}
                onChange={(date) => {
                  setFormData(prev => ({
                    ...prev,
                    contractEndDate: date
                  }));
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.87) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main !important',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6) !important',
                      },
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87) !important',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          {['Gas', 'Electricity', 'Water'].includes(formData.serviceType) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={formData.serviceType === 'Gas' ? 'MPRN' :
                       formData.serviceType === 'Electricity' ? 'MPAN' :
                       'SPID'}
                name="meterDetails.meterType"
                value={formData.meterDetails?.meterType || ''}
                onChange={handleChange}
                required
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Meter Serial Number"
              name="meterDetails.location"
              value={formData.meterDetails?.location || ''}
              onChange={handleChange}
              // Meter Serial Number is optional
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Current Rate"
              name="currentRate"
              value={formData.currentRate}
              onChange={handleChange}
            />
          </Grid>
          {['Gas', 'Electricity'].includes(formData.serviceType) && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="EAC/AQ"
                name="eacAq"
                value={formData.eacAq}
                onChange={handleChange}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Sub-services</FormLabel>
              <FormGroup>
                {loading && <CircularProgress size={20} />}
                {!loading && (
                  <>
                    {}
                    {availableSubServices.length === 0 && (
                      <div>No sub-services available for this utility type</div>
                    )}
                    {availableSubServices.map((subService) => {
                      
                      return (
                        <FormControlLabel
                          key={subService._id}
                          control={
                            <Checkbox
                              checked={(formData.subServices || []).includes(subService._id)}
                              onChange={() => handleSubServiceChange(subService._id)}
                            />
                          }
                          label={subService.name}
                        />
                      );
                    })}
                  </>
                )}
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
          />
        </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button 
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          disabled={!formData.serviceType || !formData.supplier || !formData.status}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            backdropFilter: 'blur(4px)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          Add Service
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUtilityServiceDialog;
